import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { loadProject, saveProject } from '../lib/db'
import { clock, newVisit, spanOf, unscheduled, type JobRef, type Visit } from '../lib/visits'
import { finishOf, layOut, move, NOT_ROUTED, overrun, pastTheEnd, totals, type Plan, type Stop } from '../lib/dayplan'
import { Sheet } from './bits'


/**
 * PLANNING A DAY.
 *
 * ROUTE-01, and the half of it that is true today. The owner asked for a route
 * built automatically from the addresses; nothing in this product turns an
 * address into a position, so the automatic part is a geocoding service and a
 * decision about customer data leaving the app. See docs/ROUTE-PLANNER.md.
 *
 * What is left is most of the value and none of the lie. He picks the order,
 * because he knows his town. The app does the arithmetic, which is the part
 * people get wrong at six in the morning: end to end from a start time, plus
 * the travel allowance he types, and what time the day actually finishes.
 *
 * ONE BUTTON WRITES REAL VISITS. Everything above it is a sketch on a screen
 * until then, and pressing it books exactly what is shown, as asked for rather
 * than agreed, because a customer has not been told any of this.
 */
export default function DayPlan({
  items,
  day,
  onClose,
  onBooked,
}: {
  items: ProjectSummary[]
  day: string
  onClose: () => void
  onBooked: () => void
}) {
  const [startMin, setStart] = useState(7 * 60)
  const [endMin, setEnd] = useState(17 * 60)
  const [travelMin, setTravel] = useState(20)
  const [stops, setStops] = useState<Stop[]>([])
  const [busy, setBusy] = useState('')

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name

  /* The pool is exactly what the schedule already calls ready for install. */
  const pool = useMemo(() => {
    const refs: JobRef[] = items.map((j) => ({
      id: j.id,
      customer: j.customer,
      address: j.address,
      name: j.name,
      scopeApproved: j.work?.approved ?? false,
    }))
    const visits = items.flatMap((j) => j.visits ?? [])
    const chosen = new Set(stops.map((s) => s.jobId))
    return unscheduled(refs, visits)
      .filter((q) => q.kind === 'install' && !chosen.has(q.job.id))
      .map((q) => items.find((j) => j.id === q.job.id))
      .filter((j): j is ProjectSummary => !!j)
  }, [items, stops])

  const plan: Plan = { startMin, endMin, travelMin, stops }
  const rows = layOut(plan)
  const finish = finishOf(rows, startMin)
  const over = overrun(plan)
  const late = new Set(pastTheEnd(plan))
  const t = totals(plan)

  const hhmm = (min: number) =>
    `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
  const mins = (s: string) => {
    const [h, m] = s.split(':').map(Number)
    return (h || 0) * 60 + (m || 0)
  }

  /*
   * BOOKING WRITES ONE VISIT PER STOP, at the times on the screen, as asked
   * for. Nobody has told a customer any of this, and marking it agreed would
   * be the product putting words in a homeowner's mouth.
   */
  const book = async () => {
    setBusy('Booking...')
    let n = 0
    for (const r of rows) {
      const doc = await loadProject(r.jobId)
      if (!doc) continue
      const v: Visit = {
        ...newVisit({
          id: `v_${Date.now().toString(36)}_${n}`,
          jobId: r.jobId,
          kind: 'install',
          day,
          at: new Date().toISOString(),
        }),
        from: r.from,
        to: r.to,
      }
      await saveProject({ ...doc, visits: [...(doc.visits ?? []), v] })
      n++
    }
    setBusy('')
    onBooked()
    onClose()
  }

  return (
    <Sheet
      title="Plan the day"
      variant="vsheet dayplan"
      onClose={onClose}
    >
      <div className="vsheet-body">
        <div className="ws-form vsheet-form">
          <label>
            <span>Start at</span>
            <input type="time" value={hhmm(startMin)} onChange={(e) => setStart(mins(e.target.value))} />
          </label>
          <label>
            <span>Be done by</span>
            <input type="time" value={hhmm(endMin)} onChange={(e) => setEnd(mins(e.target.value))} />
          </label>
          <label className="ws-wide">
            <span>Minutes between houses</span>
            <input
              type="number"
              min={0}
              value={travelMin}
              onChange={(e) => setTravel(Number(e.target.value))}
            />
            {/*
              THE ONE SENTENCE THIS SCREEN CANNOT DO WITHOUT. A number he
              typed is local knowledge the product does not have; calling it
              a drive time would be inventing one.
            */}
            <small className="hint">
              Your allowance, not a measured drive time. Nothing here knows how far apart these
              houses are.
            </small>
          </label>
        </div>

        {/* ------------------------------------------------ the day --- */}

        <p className="ws-eyebrow dayplan-h">The day</p>
        {rows.length === 0 ? (
          <p className="hint">Nothing on it yet. Add a house from the list below.</p>
        ) : (
          <div className="dayrows">
            {rows.map((r, i) => (
              <div key={r.jobId} className={late.has(r.jobId) ? 'dayrow late' : 'dayrow'}>
                <span className="dayrow-when">
                  {clock(r.from)}
                  <em>{clock(r.to)}</em>
                </span>
                <span className="dayrow-who">
                  <strong>{r.label}</strong>
                  <span>
                    {spanOf(r.minutes)}
                    {r.travelBefore > 0 && ` · ${r.travelBefore} min allowed to get here`}
                    {late.has(r.jobId) && ' · past the hour you wanted to finish'}
                  </span>
                </span>
                <span className="dayrow-acts">
                  <button
                    className="quiet-drop"
                    disabled={i === 0}
                    aria-label={`Move ${r.label} earlier`}
                    onClick={() => setStops(move(stops, r.jobId, -1))}
                  >
                    Up
                  </button>
                  <button
                    className="quiet-drop"
                    disabled={i === rows.length - 1}
                    aria-label={`Move ${r.label} later`}
                    onClick={() => setStops(move(stops, r.jobId, 1))}
                  >
                    Down
                  </button>
                  <button
                    className="quiet-drop"
                    onClick={() => setStops(stops.filter((s) => s.jobId !== r.jobId))}
                  >
                    Off
                  </button>
                </span>
                <label className="dayrow-hrs">
                  <span>Hours</span>
                  <input
                    type="number"
                    min={0}
                    step="0.5"
                    value={r.minutes / 60}
                    onChange={(e) =>
                      setStops(
                        stops.map((s) =>
                          s.jobId === r.jobId
                            ? { ...s, minutes: Math.round(Number(e.target.value) * 60) }
                            : s,
                        ),
                      )
                    }
                  />
                </label>
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <p className={over > 0 ? 'dayplan-sum over' : 'dayplan-sum'}>
            <b>Finishes {clock(finish)}.</b> {spanOf(t.work)} of work and {spanOf(t.travel)}{' '}
            allowed for getting between them.
            {over > 0
              ? ` That is ${spanOf(over)} past ${clock(endMin)}.`
              : ` ${spanOf(-over)} to spare.`}
          </p>
        )}

        {/* ------------------------------------------- what is ready --- */}

        <p className="ws-eyebrow dayplan-h">Ready for install</p>
        {pool.length === 0 ? (
          <p className="hint">
            {stops.length > 0
              ? 'Everything with an agreed scope and no install booked is already on the day.'
              : 'Nothing has an agreed scope and no install booked.'}
          </p>
        ) : (
          <div className="daypool">
            {pool.map((j) => (
              <button
                key={j.id}
                className="poolchip"
                onClick={() =>
                  setStops([...stops, { jobId: j.id, label: who(j), minutes: 180 }])
                }
              >
                {who(j)}
                <em>add</em>
              </button>
            ))}
          </div>
        )}
      </div>

      <p className="vsheet-unchecked">
        <b>What this has not worked out.</b> {NOT_ROUTED.join(' ')}
      </p>

      <div className="ws-actions">
        {busy && <span className="ws-said">{busy}</span>}
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={rows.length === 0 || !!busy} onClick={() => void book()}>
          Book {rows.length === 1 ? 'this stop' : `these ${rows.length} stops`}
        </button>
      </div>
    </Sheet>
  )
}
